import {
  ApiFlightItem,
  ApiInsurance,
  ApiTransfer,
  ApiComponent,
  ApiComponentType,
  ApiExtraService,
  ApiExtraUnit,
  ApiHotel,
  ApiHotelRoom,
  ApiItem,
  ApiItemType,
  ApiPackageModel,
  ApiPrice,
  ApiRoomRate,
  ApiUnitRate,
  ApiTransferUnit,
  ApiInsuranceUnit,
  ApiPackageCart,
  ApiPackageDetails
} from '@ibe/api';
import dayjs from 'dayjs';
import { clone, PriceFactory } from '@ibe/services';
import { ItemTypesEnum } from '../../../../Models/ItemTypesEnum';
import { ReactSelectOption } from '../../../../Models/ReactSelectOption';

abstract class PackageUtil {
  public static getAllMandatoryComponentsFromPackage(
    packageModel: ApiPackageModel
  ): ApiComponent[] {
    return this.getAllMandatoryComponents(this.getAllComponentsFromPackage(packageModel));
  }

  public static getComponentFinalItemType(component: ApiComponent): ApiItemType {
    const itemType = component.subItemType || component.itemType;
    return (
      (itemType === ApiItemType.TRANSFER && ApiItemType.TRANSPORT) ||
      (itemType === ApiItemType.CAR && ApiItemType.TRANSPORT) ||
      (itemType === ApiItemType.CHAUFFEUREDSERVICE && ApiItemType.TRANSPORT) ||
      (itemType === ApiItemType.AIRPORTTRANSFER && ApiItemType.TRANSPORT) ||
      (itemType === ApiItemType.MEETANDGREET && ApiItemType.EXTRA) ||
      (itemType === ApiItemType.LOUNGEACCESS && ApiItemType.EXTRA) ||
      (itemType === ApiItemType.EXTRA && ApiItemType.TOUR) ||
      itemType
    );
  }

  public static getAllOptionalComponentsFromPackage(packageModel: ApiPackageModel): ApiComponent[] {
    return this.getAllOptionalComponents(this.getAllComponentsFromPackage(packageModel));
  }

  public static getAllMandatoryComponents(components: ApiComponent[]): ApiComponent[] {
    return components.filter(component => this.isMandatory(component));
  }

  public static getAllOptionalComponents(components: ApiComponent[]): ApiComponent[] {
    return components.filter(component => !this.isMandatory(component));
  }

  public static getAllComponentsFromPackage(packageModel: ApiPackageModel): ApiComponent[] {
    return packageModel?.packageDetails?.[0].components || [];
  }

  public static isMandatory(component: ApiComponent): boolean {
    return (
      ApiComponentType.REQUIRED === component.componentType ||
      ApiComponentType.MAIN === component.componentType
    );
  }

  public static getAllComponentsOfTheSameItemSubType(
    packageModel: ApiPackageModel,
    itemTypes: string[]
  ): ApiComponent[] {
    return this.getAllComponentsFromPackage(packageModel).filter(component =>
      itemTypes.includes(this.getComponentFinalItemType(component))
    );
  }

  public static getUnselectedMandatoryItemTypes(packageModel: ApiPackageModel): ApiItemType[] {
    return PackageUtil.getAllComponentsFromPackage(packageModel).reduce(
      (total: ApiItemType[], current) => {
        return !total.includes(current.itemType) &&
          PackageUtil.isMandatory(current) &&
          current.selectedItems.length === 0
          ? [...total, current.itemType]
          : [...total];
      },
      []
    );
  }

  public static getSortedAllComponentsOfTheSameItemSubType(
    packageModel: ApiPackageModel,
    itemTypes: string[]
  ): ApiComponent[] {
    const tempArray = this.getAllComponentsFromPackage(packageModel).filter(component =>
      itemTypes.includes(this.getComponentFinalItemType(component))
    );

    return [
      ...this.getAllMandatoryComponents(tempArray),
      ...this.getAllOptionalComponents(tempArray)
    ];
  }

  public static getSortedComponents(components: ApiComponent[]): ApiComponent[] {
    return [
      ...this.getAllMandatoryComponents(components),
      ...this.getAllOptionalComponents(components)
    ];
  }

  public static getDayNumber(referenceDate: string, componentDate: string): number {
    return dayjs(componentDate).diff(dayjs(referenceDate), 'd') + 1;
  }

  public static areComponentDatesSame(component: ApiComponent): boolean {
    return dayjs(component.startDate).isSame(dayjs(component.endDate));
  }

  public static getFirstDateForGivenItemType(
    packageModel: ApiPackageModel,
    itemTypes: ApiItemType[]
  ): string {
    const components = this.getAllComponentsOfTheSameItemSubType(packageModel, itemTypes);

    const componentsStartDates: string[] = [];
    components.forEach(component => {
      componentsStartDates.push(component.startDate);
    });

    if (componentsStartDates.length > 0) {
      return componentsStartDates.reduce((pre, cur) => {
        return Date.parse(pre) > Date.parse(cur) ? cur : pre;
      });
    }

    return '';
  }

  public static getLastDateForGivenItemType(
    packageModel: ApiPackageModel,
    itemTypes: ApiItemType[]
  ): string {
    const components = this.getAllComponentsOfTheSameItemSubType(packageModel, itemTypes);

    const componentsStartDates: string[] = [];
    components.forEach(component => {
      componentsStartDates.push(component.endDate);
    });

    if (componentsStartDates.length > 0) {
      return componentsStartDates.reduce((pre, cur) => {
        return Date.parse(pre) < Date.parse(cur) ? cur : pre;
      });
    }

    return '';
  }

  public static getDateOfPackage(components: ApiComponent[], type: 'first' | 'last'): string {
    let date = '';
    if (components[0]) date = type === 'first' ? components[0].startDate : components[0].endDate;
    return components.reduce((final: string, current: ApiComponent) => {
      if (type === 'first') {
        return Date.parse(final) > Date.parse(current.startDate) ? current.startDate : final;
      }
      return Date.parse(final) < Date.parse(current.endDate) ? current.endDate : final;
    }, date);
  }

  public static getFirstDateOfPackage(packageModel: ApiPackageModel): string {
    return this.getDateOfPackage(this.getAllComponentsFromPackage(packageModel), 'first');
  }

  public static getLastDateOfPackage(packageModel: ApiPackageModel): string {
    return this.getDateOfPackage(this.getAllComponentsFromPackage(packageModel), 'last');
  }

  public static getNumberOfMandatoryItems(components: ApiComponent[]): number {
    return this.getAllMandatoryComponents(components).length;
  }

  public static getAllFlights(packageModel: ApiPackageModel): ApiComponent[] {
    const allFlightcomponents: ApiComponent[] =
      (clone(
        this.getAllComponentsOfTheSameItemSubType(packageModel, [ApiItemType.FLIGHT])
      ) as ApiComponent[]) || [];
    const combinedFlightComponents = [];
    for (let i = 0; i < allFlightcomponents.length; i += 1) {
      const currentComponent = allFlightcomponents[i];
      if (currentComponent.relatedComponentId === undefined) {
        const relatedComponent = allFlightcomponents.find(
          comp => comp.relatedComponentId === currentComponent.id
        );
        if (relatedComponent) {
          currentComponent.endDate = relatedComponent.endDate;
        }
        combinedFlightComponents.push(currentComponent);
      }
    }
    return combinedFlightComponents;
  }

  public static getAllHotels(packageModel: ApiPackageModel): ApiComponent[] {
    return this.getAllComponentsOfTheSameItemSubType(packageModel, [ApiItemType.HOTEL]) || [];
  }

  public static getAllTours(packageModel: ApiPackageModel): ApiComponent[] {
    return this.getAllComponentsOfTheSameItemSubType(packageModel, [ApiItemType.TOUR]) || [];
  }

  public static getAllTransports(packageModel: ApiPackageModel): ApiComponent[] {
    return (
      PackageUtil.getAllComponentsOfTheSameItemSubType(packageModel, [
        ApiItemType.TRANSPORT,
        ApiItemType.CHAUFFEUREDSERVICE,
        ApiItemType.AIRPORTTRANSFER
      ]) || []
    );
  }

  public static getAllRestaurants(packageModel: ApiPackageModel): ApiComponent[] {
    return (
      PackageUtil.getAllComponentsOfTheSameItemSubType(packageModel, [ApiItemType.DINING]) || []
    );
  }

  public static getAllExtras(packageModel: ApiPackageModel): ApiComponent[] {
    return (
      PackageUtil.getAllComponentsOfTheSameItemSubType(packageModel, [
        ApiItemType.EXTRA,
        ApiItemType.MEETANDGREET,
        ApiItemType.LOUNGEACCESS
      ]) || []
    );
  }

  public static getAllInsurances(packageModel: ApiPackageModel): ApiComponent[] {
    return (
      PackageUtil.getAllComponentsOfTheSameItemSubType(packageModel, [ApiItemType.INSURANCE]) || []
    );
  }

  public static getComponentsOfGivenType(
    packageModel: ApiPackageModel,
    type: ApiItemType
  ): ApiComponent[] {
    let allComponents: ApiComponent[];

    switch (type) {
      case ApiItemType.FLIGHT:
        allComponents = this.getAllFlights(packageModel);
        break;
      case ApiItemType.HOTEL:
        allComponents = this.getAllHotels(packageModel);
        break;
      case ApiItemType.TOUR:
        allComponents = this.getAllTours(packageModel);
        break;
      case ApiItemType.TRANSPORT:
        allComponents = this.getAllTransports(packageModel);
        break;
      case ApiItemType.DINING:
        allComponents = this.getAllRestaurants(packageModel);
        break;
      case ApiItemType.EXTRA:
        allComponents = this.getAllExtras(packageModel);
        break;
      case ApiItemType.INSURANCE:
        allComponents = this.getAllInsurances(packageModel);
        break;
      default:
        allComponents = [];
        break;
    }

    return this.getSortedComponents(allComponents);
  }

  public static getItemNames(item: ApiItem): string[] {
    const type = item.type || null;

    if (type === ItemTypesEnum.FLIGHT) {
      return [item.name];
    }
    if (type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).units.map((unit: ApiTransferUnit) => unit.name);
    }
    if (type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).rooms.map((room: ApiHotelRoom) => room.name);
    }
    if (type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).extraUnits.map((extraUnit: ApiExtraUnit) => extraUnit.name);
    }
    if (type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).units.map((unit: ApiInsuranceUnit) => unit.name);
    }
    if (type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).extraUnits.map((extraUnit: ApiExtraUnit) => extraUnit.name);
    }
    return [];
  }

  public static isFlightRoundtrip(item: ApiFlightItem): boolean {
    return item.segment.length > 1;
  }

  // todo: all of this only works with one unit
  public static getItemDescription(item: ApiItem): string | null {
    const type = item.type || null;

    if (type === ItemTypesEnum.FLIGHT) {
      return item.description;
    }
    if (type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).units[0].description;
    }
    if (type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).rooms[0].description;
    }
    if (type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).extraUnits[0].description;
    }
    if (type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).units[0].description;
    }
    if (type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).extraUnits[0].description;
    }

    return null;
  }

  public static getItemsToDisplayWithSelectedAsFirst<G extends { id: string }>(
    selectedItemId: string | undefined,
    items: G[]
  ): G[] {
    if (selectedItemId) {
      let selectedItem;
      const allOtherItems: G[] = [];
      items.forEach(item => {
        if (item.id === selectedItemId) {
          selectedItem = item;
        } else {
          allOtherItems.push(item);
        }
      });
      if (selectedItem) {
        return [selectedItem, ...allOtherItems];
      }
    }
    return items;
  }

  public static getDurationInDays(start: string, end: string): number {
    return dayjs(end).diff(dayjs(start), 'day');
  }

  public static getCheapestRoomRate(rates: ApiRoomRate[]): ApiRoomRate {
    return rates.sort(
      (firstItem, secondItem) => firstItem.totalPrice.finalPrice - secondItem.totalPrice.finalPrice
    )[0];
  }

  public static getCheapestUnitRate(rates: ApiUnitRate[]): ApiUnitRate {
    if (!rates.every(rate => rate?.totalPrice)) return rates[0];
    return rates
      .slice()
      .sort(
        (firstItem, secondItem) =>
          firstItem.totalPrice.finalPrice - secondItem.totalPrice.finalPrice
      )[0];
  }

  public static sortRooms(room1: ApiHotelRoom, room2: ApiHotelRoom): number {
    const cheapestRoomRateFromRoom1 = PackageUtil.getCheapestRoomRate(room1.roomrates);
    const cheapestRoomRateFromRoom2 = PackageUtil.getCheapestRoomRate(room2.roomrates);

    return (
      cheapestRoomRateFromRoom1.totalPrice.finalPrice -
      cheapestRoomRateFromRoom2.totalPrice.finalPrice
    );
  }

  public static sortExtraUnits(extraUnit1: ApiExtraUnit, extraUnit2: ApiExtraUnit): number {
    return extraUnit1.price &&
      extraUnit2.price &&
      extraUnit1.price.finalPrice &&
      extraUnit2.price.finalPrice
      ? extraUnit1.price.finalPrice - extraUnit2.price.finalPrice
      : 0;
  }

  public static getCheapestRoomRateFromHotel(hotel: ApiHotel): ApiRoomRate {
    return this.getCheapestRoomRate(hotel.rooms.sort(this.sortRooms)[0].roomrates);
  }

  public static getCheapestUnitRateFromExtra(extra: ApiExtraService): ApiUnitRate | undefined {
    const unitRatesOfCheapestElement = extra.extraUnits.sort(this.sortExtraUnits)[0].unitRates;
    if (unitRatesOfCheapestElement) {
      return this.getCheapestUnitRate(unitRatesOfCheapestElement);
    }
    return undefined;
  }

  public static getPriceOfCheapestOptionFromHotel(hotel: ApiHotel): ApiPrice {
    return PackageUtil.getCheapestRoomRateFromHotel(hotel).totalPrice;
  }

  public static getPriceOfCheapestOptionFromExtra(
    extra: ApiExtraService,
    currencyCode: string
  ): ApiPrice {
    return (
      PackageUtil.getCheapestUnitRateFromExtra(extra)?.totalPrice ||
      PriceFactory.create(0, currencyCode)
    );
  }

  public static groupRatesByRoomConfig(values: ApiRoomRate[]): Map<number, ApiRoomRate[]> {
    const groupedRoomRates = new Map<number, ApiRoomRate[]>();
    values.forEach(item => {
      const key = item.roomNumber;
      if (groupedRoomRates.has(key)) {
        groupedRoomRates.get(key)?.push(item);
      } else {
        groupedRoomRates.set(key, [item]);
      }
    });

    return new Map([...groupedRoomRates.entries()].sort());
  }

  public static buildTimeOptions(): ReactSelectOption[] {
    const times: ReactSelectOption[] = [];

    const minutesInterval = 30;
    let startTime = 0;

    for (let i = 0; startTime < 24 * 60; i += 1) {
      const hour24 = Math.floor(startTime / 60);
      const minutesMM = startTime % 60; // getting minutes of the hour in 0-30 format
      const time = `${`0${hour24 % 24}`.slice(-2)}:${`0${minutesMM}`.slice(-2)}`;
      times[i] = { value: time, label: time };
      startTime += minutesInterval;
    }

    return times;
  }

  public static getAllSelectedItems(packageCart: ApiPackageCart): ApiItem[] {
    const details = packageCart.packageModel.packageDetails;
    return details
      ? details
          .map(detail => detail.components.map(component => component.selectedItems))
          .flat()
          .flat()
      : [];
  }

  public static findComponentContainingItem(
    packageDetails: ApiPackageDetails | undefined,
    itemId: string
  ): ApiComponent | undefined {
    return packageDetails && packageDetails.components
      ? packageDetails.components.find(
          component =>
            !!component.selectedItems.find(item => item.id === itemId) ||
            component.selectableItems.find(item => item.id === itemId)
        )
      : undefined;
  }
}

export default PackageUtil;
